/**
 * Centralized Zip Code Manager
 * Manages zip code, state, and growing zone across all components
 */

(function() {
  'use strict';

  // Custom event names
  const EVENTS = {
    ZIPCODE_UPDATED: 'zipcodeUpdated',
    ZIPCODE_UPDATE_START: 'zipcodeUpdateStart',
    ZIPCODE_UPDATE_ERROR: 'zipcodeUpdateError'
  };

  // LocalStorage keys
  const STORAGE_KEYS = {
    ZIP_CODE: 'zip_code',
    USER_STATE: 'user_state',
    GROWING_ZONE: 'growing_zone',
    SHIPPING_RATES: 'shipping_rates_v2_'
  };

  /**
   * Get stored zip code from localStorage
   */
  function getStoredZipCode() {
    return localStorage.getItem(STORAGE_KEYS.ZIP_CODE);
  }

  /**
   * Get stored state from localStorage
   */
  function getStoredState() {
    return localStorage.getItem(STORAGE_KEYS.USER_STATE);
  }

  /**
   * Get stored growing zone from localStorage
   */
  function getStoredGrowingZone() {
    return localStorage.getItem(STORAGE_KEYS.GROWING_ZONE);
  }

  /**
   * Get stored shipping rates for a specific zip code
   */
  function getStoredShippingRates(zipCode) {
    const rates = localStorage.getItem(STORAGE_KEYS.SHIPPING_RATES + zipCode);
    return rates ? JSON.parse(rates) : null;
  }

  /**
   * Build payload for cart "estimated shipping" line (first paid rate only). Free-only options return null — the free-shipping bar already covers that.
   * @param {{ shipping_rates?: Array<{ name?: string, price?: string|number }> }} ratesResult
   * @returns {{ mode: 'paid', formattedPrice: string } | null}
   */
  function getProductShippingEstimatePayload(ratesResult) {
    if (!ratesResult || !ratesResult.shipping_rates || !ratesResult.shipping_rates.length) {
      return null;
    }
    const rates = ratesResult.shipping_rates;
    for (let i = 0; i < rates.length; i++) {
      const r = rates[i];
      if (r.name && !r.name.includes('Free Shipping')) {
        const raw = r.price;
        const num = typeof raw === 'string' ? parseFloat(raw, 10) : Number(raw);
        if (Number.isNaN(num)) {
          return null;
        }
        const formatted = num.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
        });
        return { mode: 'paid', formattedPrice: formatted };
      }
    }
    return null;
  }

  /**
   * Validate zip code format
   */
  function isValidZipCode(zipCode) {
    return /^\d{5}$/.test(zipCode);
  }

  /**
   * Fetch state by zip code using zippopotam.us API
   */
  function fetchStateByZip(zipCode) {
    return fetch(`https://api.zippopotam.us/us/${zipCode}`)
      .then(res => {
        if (!res.ok) throw new Error('Invalid zip code');
        return res.json();
      })
      .then(data => {
        if (data.places && data.places.length > 0) {
          return data.places[0]['state abbreviation'];
        }
        return null;
      })
      .catch(() => null);
  }

  /**
   * Fetch growing zone by zip code using phzmapi.org API
   */
  function fetchGrowingZoneByZip(zipCode) {
    return fetch(`https://phzmapi.org/${zipCode}.json`)
      .then(res => res.json())
      .then(data => {
        if (data.zone) {
          return {
            zone: data.zone,
            state: data.state || null
          };
        }
        return { zone: null, state: data.state || null };
      })
      .catch(() => ({ zone: null, state: null }));
  }

  /**
   * Update zip code and fetch all related data
   * @param {string} zipCode - The zip code to save
   * @param {Object} options - Optional parameters
   * @param {string} options.state - Pre-fetched state (optional)
   * @param {string} options.zone - Pre-fetched zone (optional)
   * @param {boolean} options.fetchState - Whether to fetch state (default: true)
   * @param {boolean} options.fetchZone - Whether to fetch zone (default: true)
   * @returns {Promise}
   */
  async function updateZipCode(zipCode, options = {}) {
    const trimmedZip = String(zipCode).trim();

    if (!isValidZipCode(trimmedZip)) {
      const error = new Error('Invalid zip code format. Must be 5 digits.');
      dispatchEvent(EVENTS.ZIPCODE_UPDATE_ERROR, { error: error.message, zipCode: trimmedZip });
      throw error;
    }

    // Dispatch start event
    dispatchEvent(EVENTS.ZIPCODE_UPDATE_START, { zipCode: trimmedZip });

    try {
      let state = options.state || null;
      let zone = options.zone || null;

      // Fetch growing zone (includes state)
      if (options.fetchZone !== false) {
        const zoneData = await fetchGrowingZoneByZip(trimmedZip);
        zone = zoneData.zone;
        if (!state && zoneData.state) {
          state = zoneData.state;
        }
      }

      // Fetch state if not provided and not fetched from zone API
      if (!state && options.fetchState !== false) {
        state = await fetchStateByZip(trimmedZip);
      }

      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.ZIP_CODE, trimmedZip);
      
      if (state) {
        localStorage.setItem(STORAGE_KEYS.USER_STATE, state);
      }
      
      if (zone) {
        localStorage.setItem(STORAGE_KEYS.GROWING_ZONE, zone);
      } else {
        localStorage.removeItem(STORAGE_KEYS.GROWING_ZONE);
      }

      // Dispatch success event
      const eventData = {
        zipCode: trimmedZip,
        state: state,
        zone: zone
      };
      
      dispatchEvent(EVENTS.ZIPCODE_UPDATED, eventData);

      return eventData;
    } catch (error) {
      dispatchEvent(EVENTS.ZIPCODE_UPDATE_ERROR, { error: error.message, zipCode: trimmedZip });
      throw error;
    }
  }

  /**
   * Fetch location by IP using geolocation API
   * @param {string} apiKey - The geolocation API key
   */
  function fetchLocationByIP(apiKey) {
    if (!apiKey) {
      return Promise.reject(new Error('API key is required'));
    }

    return fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${apiKey}`)
      .then(res => res.json())
      .then(data => {
        if (data.country_name === 'United States' && data.zipcode) {
          return {
            zipCode: data.zipcode,
            state: data.state_prov || null,
            supported: true
          };
        }
        return {
          zipCode: null,
          state: null,
          supported: false
        };
      });
  }

  /**
   * Update zip code from geolocation
   * @param {string} apiKey - The geolocation API key
   */
  async function updateZipCodeFromGeolocation(apiKey) {
    const location = await fetchLocationByIP(apiKey);
    
    if (!location.supported) {
      throw new Error('This location is not supported');
    }

    return updateZipCode(location.zipCode, {
      state: location.state,
      fetchState: false,
      fetchZone: true
    });
  }

  /**
   * Clear all stored zip code data
   */
  function clearZipCodeData() {
    localStorage.removeItem(STORAGE_KEYS.ZIP_CODE);
    localStorage.removeItem(STORAGE_KEYS.USER_STATE);
    localStorage.removeItem(STORAGE_KEYS.GROWING_ZONE);
    
    // Clear all shipping rates
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(STORAGE_KEYS.SHIPPING_RATES)) {
        localStorage.removeItem(key);
      }
    });

    dispatchEvent(EVENTS.ZIPCODE_UPDATED, {
      zipCode: null,
      state: null,
      zone: null
    });
  }

  /**
   * Dispatch custom event
   */
  function dispatchEvent(eventName, detail) {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /**
   * Add event listener for zip code updates
   */
  function onZipCodeUpdate(callback) {
    window.addEventListener(EVENTS.ZIPCODE_UPDATED, (e) => callback(e.detail));
  }

  /**
   * Add event listener for zip code update errors
   */
  function onZipCodeError(callback) {
    window.addEventListener(EVENTS.ZIPCODE_UPDATE_ERROR, (e) => callback(e.detail));
  }

  /**
   * Add event listener for zip code update start
   */
  function onZipCodeUpdateStart(callback) {
    window.addEventListener(EVENTS.ZIPCODE_UPDATE_START, (e) => callback(e.detail));
  }

  // Export to window
  window.ZipCodeManager = {
    // Methods
    updateZipCode,
    updateZipCodeFromGeolocation,
    clearZipCodeData,
    
    // Getters
    getStoredZipCode,
    getStoredState,
    getStoredGrowingZone,
    getStoredShippingRates,
    getProductShippingEstimatePayload,

    // Validators
    isValidZipCode,
    
    // API Helpers
    fetchStateByZip,
    fetchGrowingZoneByZip,
    fetchLocationByIP,
    
    // Event Listeners
    onZipCodeUpdate,
    onZipCodeError,
    onZipCodeUpdateStart,
    
    // Constants
    EVENTS,
    STORAGE_KEYS
  };

})();

